import { makeAutoObservable } from "mobx";
import { IRmaListItem, RMA_PROVIDERS, RMA_STATUS_TRANSLATION_KEY } from "../../../../services/api/rma-list-service";
import { TOOLTIP_VARIANTS } from "../../../../components/tooltip/tooltip";
import { RMA_STATUS } from "../../rma-page/rma-types";
import currentPortalStore from "../../../../stores/current-portal-store";
import { formatDateTimeWithFallback } from "../../../../utilities/date-utilities";
import { DATETIME_VIEW } from "../../../../components/datetime-v2/datetime-v2";
import dashboardSettingsStore from "../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../models/ProfileCapabilities";
import translationStore from "../../../../translation/stores/translations-store";

const MIXED_RESOLUTION_LABEL = 'Mix';
const RESOLUTIONS_TRANSLATION_SCOPE = 'resolutions';

export interface IReservedItemsInfo {
    iconColor: string;
    tooltipVariant: TOOLTIP_VARIANTS;
    tooltipText: string;
}

class RmaListItemStore {
    private _model: IRmaListItem;

    constructor(model: IRmaListItem) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._model = ensureValidModelCopy(model);
    }

    public get rmaId(): number { return this._model.id; }
    public get orderName(): string { return this._model.order_name; }
    public get orderUrl(): string { return this._model._order_url; }
    public get customerName(): string { return this._model._customer_name; }
    public get customerUrl(): string { return this._model._customer_url; }
    public get dateString(): string { return this._model.date_to_show; }
    public get amount(): number { return this._model.amount; }
    public get statusTranslationKey(): RMA_STATUS_TRANSLATION_KEY { return this._model._status; }
    public get imagesUrls(): string[] { return this._model._images_urls; }
    public get isGift(): boolean { return this._model.is_gift; }
    public get isOutOfPolicy(): boolean { return this._model.is_out_of_policy; }
    public get lastUnresolvedError(): string | null { return this._model.last_unresolved_error; }
    public get exchangeDraftOrderId(): number|null { return this._model._exchange_draft_order_id; }
    public get reserveItemCount(): number { return this._model.reserve_item_count; }
    public get reserveUntilDate(): string | null { return this._model._reserve_until_date; }
    public get releaseDate(): string | null { return this._model._release_date; }
    public get itemReservationTimeframe(): number { return this._model._item_reservation_timeframe; }
    public get showReserveItemsIndicator(): boolean {
        return this.reserveUntilDate !== null && this._model.reserve_item_count > 0 && !this._model._is_instant_exchange
    }
    public get resolutionName(): string {
        return this._resolveMerchantResolutionLabel({
            nameKey: this._model._resolution_name_key,
            internalName: this._model._resolution_internal_name,
            hasMultipleResolutions: this._model._has_multiple_resolutions,
        });
    }
    public get isRreturngoResolution(): boolean { return this._model.is_returngo_resolution; }
    public get hasExchange(): boolean { return this._model.has_exchange; }
    public get refundProvider(): RMA_PROVIDERS | null { return this._model._refund_provider; }
    public get creditIntegrationProvider(): RMA_PROVIDERS | null { return this._model._credit_integration_provider; }
    public get noOffers(): number | null { return this._model.no_offers; }

    private get _isPending(): boolean { return this._model.status === RMA_STATUS.PENDING_APPROVAL; }
    private get _isReserveReleased(): boolean { return !this._isReserveExpired && this.releaseDate !== null; }
    private get _isReserveExpired(): boolean { 
        if (this.reserveUntilDate === null) {
            return false;
        }

        /** if already released - check if released before or after reservation ended */
        if (this.releaseDate !== null) {
            return (new Date(this.reserveUntilDate)).getTime() < (new Date(this.releaseDate)).getTime();    
        }

        /** if not yet released - check if reservation time is not in past */
        return (new Date(this.reserveUntilDate)).getTime() < (new Date()).getTime();
    }

    /** 
     * Implementation of this method should be aligned with "reservedItemsInfo" method  
     * in RmaExchangeOrderStore: src/pages/manage-returns/rma-page/stores/rma-exchange-order-store.ts
     */
    public get reservedItemsInfo(): IReservedItemsInfo|null {
        /** 
         * if exchange order does not have reserved items, 
         * or it is instant exchange - we should not return any information 
         * */
        if (!this.showReserveItemsIndicator) {
            return null;
        }

        const itemsTitle = this.reserveItemCount > 1 ? `items` : 'item';
        const toBeTitle = this.reserveItemCount > 1 ? 'are' : 'is';
        const toBePastTitle = this.reserveItemCount > 1 ? 'were' : 'was';

        const draftorderId = this.exchangeDraftOrderId;
        const reservationDaysCount = this.itemReservationTimeframe;
        const reservationdaysTitle = reservationDaysCount > 1 ? 'days' : 'day';

        const tzStore = currentPortalStore.setting?.timezoneStore;
        const formattedUntilDateTime = formatDateTimeWithFallback(tzStore, this.reserveUntilDate, DATETIME_VIEW.FULL);
        const formattedReleasedDateTime = formatDateTimeWithFallback(tzStore, this.releaseDate, DATETIME_VIEW.FULL);

        if (this._isPending) {
            return {
                iconColor: 'var(--color-accent-1-50)',
                tooltipVariant: TOOLTIP_VARIANTS.INFO,
                tooltipText: `Once approved, ${this.reserveItemCount} ${itemsTitle} will be reserved for ${reservationDaysCount} ${reservationdaysTitle}.`
            };
        }

        if (this._isReserveExpired) {
            const reservationMessage = (this.releaseDate !== null) 
                ? `Despite the reservation period expiring, the exchange was successfully released on ${formattedReleasedDateTime}, as the ${itemsTitle} ${toBePastTitle} in stock.`
                : `${this.reserveItemCount} reserved ${itemsTitle} in draft order ${draftorderId} ${toBePastTitle} restocked on ${formattedUntilDateTime}.`;

            return {
                iconColor: 'var(--color-semantic-danger)',
                tooltipVariant: TOOLTIP_VARIANTS.ERROR,
                tooltipText: reservationMessage
            };
        }

        if (this._isReserveReleased) {
            return {
                iconColor: 'var(--color-semantic-success)',
                tooltipVariant: TOOLTIP_VARIANTS.SUCCESS,
                tooltipText: `${this.reserveItemCount} reserved ${itemsTitle} in draft order ${draftorderId} ${toBePastTitle} released on ${formattedReleasedDateTime}.`
            };
        }

        return {
            iconColor: 'var(--color-accent-1)',
            tooltipVariant: TOOLTIP_VARIANTS.INFO,
            tooltipText: `${this.reserveItemCount} ${itemsTitle} ${toBeTitle} reserved for draft order ${draftorderId} until ${formattedUntilDateTime}.`
        };
    }

    private _resolveMerchantResolutionLabel({
        nameKey,
        internalName,
        hasMultipleResolutions = false,
    }: {
        nameKey: string;
        internalName: string;
        hasMultipleResolutions?: boolean;
    }): string {
        if (hasMultipleResolutions) {
            return MIXED_RESOLUTION_LABEL;
        }

        const portalTranslations = dashboardSettingsStore.resolveCapability(
            PROFILE_CAPABILITIES.PORTAL_TRANSLATIONS
        );
        if (portalTranslations?.isRefundMethodsScopeSupported === true) {
            const trimmedInternalName = internalName?.trim() ?? '';
            return trimmedInternalName !== '' ? trimmedInternalName : nameKey;
        }

        const scopeStore = translationStore.loadScope(RESOLUTIONS_TRANSLATION_SCOPE);
        return scopeStore.getDefaultTranslation(nameKey) ?? nameKey;
    }
}

export default RmaListItemStore;

/** some fields receievd from an api has an incorrect type */
function ensureValidModelCopy(data: IRmaListItem): IRmaListItem {
    const toBool = (v: any) => Boolean(v);
    return {
        ...data,
        is_gift: toBool(data.is_gift),
        is_out_of_policy: toBool(data.is_out_of_policy),
    };
}